// Auth state derived from the server. `me` is the single source of truth: a successful
// query means an authenticated session, a 401 means logged out.

import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { api, ApiError } from '../api/client';
import type { Me } from '../api/bindings/Me';

interface AuthState {
  user: Me | null;
  loading: boolean;
  refresh: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    // A 401 is a normal "logged out" answer, not a retryable error.
    retry: (_count, err) => !(err instanceof ApiError && err.status === 401),
  });

  const logoutMutation = useMutation({
    mutationFn: api.logout,
    onSuccess: () => qc.setQueryData(['me'], null),
  });

  const unauthorized = meQuery.error instanceof ApiError && meQuery.error.status === 401;

  const value: AuthState = {
    user: unauthorized ? null : (meQuery.data ?? null),
    loading: meQuery.isLoading,
    refresh: () => qc.invalidateQueries({ queryKey: ['me'] }),
    logout: () => logoutMutation.mutate(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
